import {
  CustomTableCell,
  CustomTableHead,
  Dialog,
  
  FormDatePicker,
  FormInput,
  FormLabel,
  FormSectionHeader,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import useAddEdit from "./useAddEdit";
import { FormProvider } from "react-hook-form";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@/components/ui";
import {
  BILLING_AMOUNT,
  BILLING_CATEGORY,
  BILLING_DATE,
  BILLING_MONTH_YEAR,
  COMMENT,
  CURR_OWNER_ID,
  CURR_OWNER_NAME,
  DOCUMENT_ID,
  DOCUMENT_TYPE,
  DRCR,
  DUE_DATE,
  GL_ACCOUNT,
  LINE_BILLING_AMOUNT,
  LINE_ITEM_TYPE,
  LINE_ITEMS,
  PAYMENT_TERM,
  PROPERTY_BLOCK,
  PROPERTY_ID,
  PROPERTY_MODEL,
  STREET_ADDRESS,
  TRANSACTION_TYPE,
  UNIT_NO,
} from "./consts";
import { cn, transactionTypeOptions } from "@/lib/utils";

const AddEdit = () => {
  const {
    breadcrumbData,
    onBackClick,
    form,
    requiredFields,
    glData,
    isDocumentTypeLoading,
    isLoadingGlMaster,
    breadcrumbTitle,
    keys,
    onSubmit,
    documentTypes,
    isPaymentTermLoading,
    paymentTermOptions,
    propertyModels,
    isPropertyModelLoading,
    billingCategory,
    isBillingCategoryLoading,
    onCancel,
    onChangeFilterCriteria,
    onApplyFilter,
    onClearFilter,
    filterCriteria,
    isLoadingBillingData,
    openPostDialog,
    setOpenPostDialog,
    onPostConfirmation,
    isPostingPending,
    hasPostPermission,
    isView,
    buttonAction,
    isUpdatePending,
    hasUpdatePermission
  } = useAddEdit();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6 !pt-0">
      <div className="flex flex-col lg:flex-row lg:items-center w-full justify-between">
        <Heading
          title={breadcrumbTitle}
          breadcrumbData={breadcrumbData}
          showBack
          onBackClick={onBackClick}
        />
      </div>

      {isLoadingGlMaster ||
      isDocumentTypeLoading ||
      isPaymentTermLoading ||
      isPropertyModelLoading ||
      isBillingCategoryLoading ||
      isLoadingBillingData ? (
        <Loader />
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[10px] rounded-lg px-[30px] py-[15px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[20px] items-center">
                <FormSectionHeader>Document Header</FormSectionHeader>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-[20px]">
                <FormSelect
                  key={keys.documentTypes}
                  name={DOCUMENT_TYPE}
                  control={form.control}
                  label="Document Type"
                  placeholder="Please select"
                  error={errors[DOCUMENT_TYPE]?.message}
                  isRequired
                  options={(documentTypes || [])?.map((data: any) => ({
                    value: data?.documentTypeId?.toString(),
                    label: data?.documentType,
                  }))}
                  disabled
                />
                <FormInput
                  name={DOCUMENT_ID}
                  control={form.control}
                  label="Document Id"
                  placeholder="Please enter"
                  error={errors[DOCUMENT_ID]?.message}
                  isRequired={requiredFields.includes(DOCUMENT_ID)}
                  disabled
                />
                <FormInput
                  name={BILLING_MONTH_YEAR}
                  control={form.control}
                  label="Billing Month/Year"
                  placeholder="Select"
                  error={errors[BILLING_MONTH_YEAR]?.message}
                  isRequired
                  disabled
                />
                <FormDatePicker
                  name={BILLING_DATE}
                  control={form.control}
                  label="Billing Date"
                  placeholder="Please select"
                  error={errors[BILLING_DATE]?.message}
                  isRequired
                  disabled={isView}
                />
                <FormInput
                  name={BILLING_AMOUNT}
                  control={form.control}
                  label="Billing Amount"
                  placeholder="Please enter"
                  error={errors[BILLING_AMOUNT]?.message}
                  isRequired
                  disabled={isView}
                  startIcon="$"
                  variant="float"
                />
                <FormDatePicker
                  name={DUE_DATE}
                  control={form.control}
                  label="Due Date"
                  placeholder="Please select"
                  error={errors[DUE_DATE]?.message}
                  isRequired
                  disabled
                />
                <FormSelect
                  key={keys.transactionType}
                  name={TRANSACTION_TYPE}
                  control={form.control}
                  label="Transaction Type"
                  placeholder="Please select"
                  error={errors[TRANSACTION_TYPE]?.message}
                  isRequired
                  options={transactionTypeOptions}
                  disabled
                />
                <FormSelect
                  key={keys.accountName}
                  name={GL_ACCOUNT}
                  control={form.control}
                  label="Gl Account"
                  placeholder="Please select"
                  error={errors[GL_ACCOUNT]?.message}
                  isRequired
                  options={(glData?.data || [])
                    ?.sort((a: any, b: any) =>
                      a.glAccountNumber?.localeCompare(b.glAccountNumber)
                    )
                    ?.map((data: any) => {
                      return {
                        label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                        value: data?.glMasterId?.toString(),
                      };
                    })}
                  disabled
                />
                <FormSelect
                  key={keys.paymentTerm}
                  name={PAYMENT_TERM}
                  control={form.control}
                  label="Payment Term"
                  placeholder="Please select"
                  error={errors[PAYMENT_TERM]?.message}
                  isRequired
                  options={(paymentTermOptions?.data || [])?.map(
                    (data: any) => ({
                      label: data?.paymentTermName,
                      value: data?.paymentTermId?.toString(),
                    })
                  )}
                  disabled
                />
                <FormInput
                  name={COMMENT}
                  control={form.control}
                  label="Header Text"
                  placeholder="Please enter"
                  error={errors[COMMENT]?.message}
                  isRequired={requiredFields.includes(COMMENT)}
                  disabled
                />
              </div>
            </div>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[15px]">
              <div className={cn("grid grid-cols-1 md:grid-cols-4 mb-3")}>
                <FormSectionHeader className="">Line Items</FormSectionHeader>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-[20px] md:col-span-3">
                  <div className="relative">
                    <FormLabel>Billing Category</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        onChangeFilterCriteria("categoryId", value)
                      }
                      key={keys.billingCategory}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue
                          placeholder={
                            filterCriteria?.categoryId
                              ? billingCategory?.data?.find(
                                  (data: any) =>
                                    data?.billingCategoryId?.toString() ==
                                    filterCriteria?.categoryId?.toString()
                                )?.categoryName
                              : "Please Select"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {billingCategory?.data?.map((data: any) => (
                          <SelectItem
                            key={data?.billingCategoryId?.toString()}
                            value={data?.billingCategoryId?.toString()}
                          >
                            {data?.categoryName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="relative">
                    <FormLabel>Property Model</FormLabel>
                    <Select
                      onValueChange={(value) =>
                        onChangeFilterCriteria("modelId", value)
                      }
                      key={keys.propertyModel}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue
                          placeholder={
                            filterCriteria?.modelId
                              ? propertyModels?.data?.find(
                                  (data: any) =>
                                    data?.modelId?.toString() ==
                                    filterCriteria?.modelId?.toString()
                                )?.modelName
                              : "Please Select"
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyModels?.data?.map((data: any) => (
                          <SelectItem
                            key={data?.modelId?.toString()}
                            value={data?.modelId?.toString()}
                          >
                            {data?.modelName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button onClick={onApplyFilter} type="button">
                    Apply
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={onClearFilter}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto w-full">
                <div className="min-w-[2000px]">
                  <Table>
                    <TableHeader>
                      <TableRow className="mb-3 rounded">
                        <CustomTableHead className="w-[150px]">
                          Property ID
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Billing Category
                        </CustomTableHead>
                        <CustomTableHead className="w-[150px]">
                          Prop Model
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Prop Block
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Unit No.
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Street Address
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Curr. Owner ID
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Current Owner Name
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Dr/Cr
                        </CustomTableHead>
                        <CustomTableHead className="w-[100px]">
                          Billing Amt.
                        </CustomTableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.watch(LINE_ITEMS)?.map((_: any, index: number) => {
                        const isOffset = _?.[LINE_ITEM_TYPE]?.toLowerCase() == "offset";
                        if (isOffset) return;
                        
                        return (
                          <TableRow
                            key={`row-${index}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${PROPERTY_ID}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    PROPERTY_ID
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${PROPERTY_ID}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                key={keys.billingCategory}
                                name={`${LINE_ITEMS}.${index}.${BILLING_CATEGORY}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    BILLING_CATEGORY
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${BILLING_CATEGORY}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                key={keys.propertyModel}
                                name={`${LINE_ITEMS}.${index}.${PROPERTY_MODEL}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    PROPERTY_MODEL
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${PROPERTY_MODEL}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${PROPERTY_BLOCK}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    PROPERTY_BLOCK
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${PROPERTY_BLOCK}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${UNIT_NO}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    UNIT_NO
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${UNIT_NO}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${STREET_ADDRESS}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    STREET_ADDRESS
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${STREET_ADDRESS}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${CURR_OWNER_ID}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    CURR_OWNER_ID
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${CURR_OWNER_ID}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${CURR_OWNER_NAME}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    CURR_OWNER_NAME
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${CURR_OWNER_NAME}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${DRCR}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    DRCR
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${DRCR}`
                                )}
                                disabled
                              />
                            </CustomTableCell>
                            <CustomTableCell>
                              <FormInput
                                name={`${LINE_ITEMS}.${index}.${LINE_BILLING_AMOUNT}`}
                                control={form.control}
                                label=""
                                placeholder="Enter"
                                error={
                                  (errors[LINE_ITEMS] as any)?.[`${index}`]?.[
                                    LINE_BILLING_AMOUNT
                                  ]?.message
                                }
                                isRequired={requiredFields.includes(
                                  `${LINE_ITEMS}.${index}.${LINE_BILLING_AMOUNT}`
                                )}
                                disabled={isView}
                                startIcon="$"
                                variant="float"
                              />
                            </CustomTableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
            <div className="flex flex-row justify-end gap-3  py-3">
              <Button
                variant="outline"
                type="button"
                onClick={onCancel}
                disabled={isPostingPending}
              >
                Cancel
              </Button>
              {isView || !hasUpdatePermission ? <></> : 
                <Button disabled={isUpdatePending} onClick={() => {
                  buttonAction.current = "park";
                }}>Park</Button>}
              {hasPostPermission ? (
                <Button disabled={isPostingPending} onClick={() => {
                  buttonAction.current = "post";
                }}>Post</Button>
              ) : (
                <></>
              )}
            </div>
          </form>
        </FormProvider>
      )}

      {openPostDialog && (
        <Dialog
          open={openPostDialog}
          setOpen={setOpenPostDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          cancelText="No"
          saveText="Yes"
          onSave={onPostConfirmation}
        >
          <p className="text-sm">Do you want to post this billing?</p>
        </Dialog>
      )}
    </div>
  );
};

export default AddEdit;
